import { Box } from '@mui/material'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useGeolocation from 'react-hook-geolocation'
import type { MapRef } from 'react-map-gl/mapbox'
import MapGL, { Marker } from 'react-map-gl/mapbox'
import { NEGATIVE_PAGE_GUTTER } from 'src/components/layout/pageGutter'
import { HEADER } from 'src/config'
import { VehiclesQuery } from 'src/generated'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import { MapControls } from './MapControls'
import MapLegend from './MapLegend'
import { MapRecentre } from './MapRecentre'
import { MapRefresh } from './MapRefresh'
import { MapSearch } from './MapSearch'
import MapVehicleSheet from './MapVehicleSheet'
import { VehicleMarker } from './VehicleMarker'
import {
  FLEET_MAX_ZOOM,
  fitPaddingFor,
  fleetView,
  locatedVehicles,
} from './bounds'
import { MapCountriesValue, OPENING_CITY, operatingCities } from './consts'

export type MapProps = {
  vehicles: VehiclesQuery['vehicles']['vehicles']
  /**
   * Whether the fleet could not be loaded. An empty list means two different
   * things, and telling an operator that no vehicle has reported a position
   * when the request failed is the wrong one.
   */
  fleetUnavailable?: boolean
  /**
   * Ask for the fleet again, so every marker moves to where its car last
   * reported. With none, the map offers no refresh.
   */
  onRefresh?: () => Promise<unknown>
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX || ''

/** How close the Center list takes the map. A city, not a street. */
const MAP_CITY_ZOOM = 10

/**
 * Where the map opens.
 *
 * Deliberately a constant. This used to read the operator's position, but
 * `initialViewState` is consumed once on mount and the geolocation hook has no
 * fix on the first render, so the branch could never run: the map has always
 * opened here. The controls above it are what move it, and the button that
 * frames the fleet is the one that answers "where are my cars".
 */
const OPENING_CENTER = operatingCities[OPENING_CITY].coordinates ?? {
  lat: 59.437,
  lng: 24.752,
}

const Map = ({ vehicles, fleetUnavailable, onRefresh }: MapProps) => {
  const geolocation = useGeolocation()
  const mapRef = useRef<MapRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  // Everything on this screen reads the same list. A vehicle whose position is
  // the schema's way of saying "no fix" is not drawn, not offered by search and
  // not framed, rather than the first two and not the third.
  const placed = useMemo(() => locatedVehicles(vehicles), [vehicles])

  const selectedVehicle = useMemo(
    () => placed.find((vehicle) => vehicle.id === selectedId),
    [placed, selectedId],
  )

  /**
   * Whether the browser has actually told us where the operator is.
   *
   * `react-hook-geolocation` types latitude as a number because it extends the
   * DOM's `GeolocationCoordinates`, but it initialises it to `null` and fills
   * it in from a `watchPosition` callback. So the null check is load bearing at
   * run time even though it reads as redundant against the types, and a
   * truthiness check would additionally treat the equator as no fix.
   */
  const hasUserLocation =
    geolocation.latitude != null && geolocation.longitude != null

  const handleCenterChange = useCallback(
    (value: MapCountriesValue) => {
      if (value === 'userLocation') {
        // The Center list offers "My location" only while there is a fix, so
        // this is reachable only in the moment permission is withdrawn. Doing
        // nothing is honest; the old code flew to Tallinn and called it the
        // operator's location.
        if (!hasUserLocation) return

        mapRef.current?.flyTo({
          center: [geolocation.longitude, geolocation.latitude],
          zoom: MAP_CITY_ZOOM,
        })
        return
      }

      const city = operatingCities[value]?.coordinates
      if (!city) return

      mapRef.current?.flyTo({
        center: [city.lng, city.lat],
        zoom: MAP_CITY_ZOOM,
      })
    },
    [geolocation.latitude, geolocation.longitude, hasUserLocation],
  )

  // The map opens on a city, and the fleet is wherever the fleet is. This puts
  // every vehicle that has reported a position into one view, which is the
  // question an operator opens the map to answer.
  const fleetFrame = useMemo(() => fleetView(placed), [placed])

  const handleFitToFleet = useCallback(() => {
    const map = mapRef.current
    if (!map) return

    if (fleetFrame.kind === 'point') {
      map.flyTo({ center: fleetFrame.center, zoom: fleetFrame.zoom })
      return
    }

    if (fleetFrame.kind === 'bounds') {
      const canvas = map.getCanvas()

      map.fitBounds([fleetFrame.southWest, fleetFrame.northEast], {
        padding: fitPaddingFor(canvas.clientWidth, canvas.clientHeight),
        maxZoom: FLEET_MAX_ZOOM,
      })
    }
  }, [fleetFrame])

  const handleSearchSelect = useCallback(
    (vehicle: VehiclesQuery['vehicles']['vehicles'][0]) => {
      const location = vehicle.latestStatus?.location
      if (location) {
        mapRef.current?.flyTo({
          center: [location.longitude, location.latitude],
          zoom: 14,
        })
      }
      setSelectedId(vehicle.id)
    },
    [],
  )

  const { height } = useWindowDimensions()

  // The map fills the viewport below the slim top bar. Subtracting the actual
  // bar height (not a stale 88px) means the map reaches the bottom edge with no
  // white strip showing through underneath.
  const mapHeight = useMemo(() => height - HEADER.MOBILE_HEIGHT, [height])

  // Mapbox sizes its canvas to the container once and doesn't react to the
  // container growing/shrinking when the nav rail collapses or the window
  // resizes. Observe the wrapper and tell the map to resize so it always fills
  // the available area.
  useEffect(() => {
    const node = containerRef.current
    if (!node) return undefined
    const observer = new ResizeObserver(() => {
      mapRef.current?.resize()
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    // The map is the page, so it bleeds to the window edges, and it does that
    // by negating the page's own gutter rather than by the page zeroing its
    // padding. Zeroing took the gutter away from everything in the container,
    // which is why this screen's heading used to sit 24px left of every other
    // screen's, flush against the nav rail. The gutter is the page's; the
    // bleed belongs to the thing that bleeds. Same value, negated, from the
    // one module that owns it.
    //
    // No `width: 100%` with it: a full-bleed box is auto width, or the
    // negative left margin shifts it without widening it and the right edge
    // lands a gutter short. This was a `Grid container`, which declares
    // `width: 100%` in its own styles and cannot be told otherwise.
    <Box
      sx={{
        position: 'relative',
        mx: NEGATIVE_PAGE_GUTTER,
        height: `${mapHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
      }}
    >
      <MapLegend />

      <MapRecentre
        onRecentre={handleFitToFleet}
        disabledReason={
          fleetUnavailable
            ? 'The fleet could not be loaded'
            : fleetFrame.kind === 'none'
              ? 'No vehicle has reported a position yet'
              : undefined
        }
      />

      <MapControls
        onChangeCountryLocationSelector={handleCenterChange}
        hasUserLocation={hasUserLocation}
        search={<MapSearch vehicles={placed} onSelect={handleSearchSelect} />}
      />

      {/* After the controls, which on a phone span the map's width: the two
          share a layer, and the later one is the one a tap reaches. */}
      {onRefresh && <MapRefresh onRefresh={onRefresh} />}
      <div ref={containerRef} style={{ flex: 1, position: 'relative' }}>
        <MapGL
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          initialViewState={{
            latitude: OPENING_CENTER.lat,
            longitude: OPENING_CENTER.lng,
            zoom: MAP_CITY_ZOOM,
          }}
          style={{ width: '100%', height: '100%', position: 'absolute' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
        >
          {placed.map(
            (mapVehicle) =>
              mapVehicle.latestStatus && (
                <Marker
                  key={mapVehicle.id}
                  latitude={mapVehicle.latestStatus.location.latitude}
                  longitude={mapVehicle.latestStatus.location.longitude}
                  anchor="center"
                >
                  <VehicleMarker
                    mapVehicle={mapVehicle}
                    selected={selectedId === mapVehicle.id}
                    onSelect={setSelectedId}
                  />
                </Marker>
              ),
          )}
        </MapGL>
        {selectedVehicle && (
          <MapVehicleSheet
            mapVehicle={selectedVehicle}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>
    </Box>
  )
}

export default Map
